import { ERROR_MESSAGE_TEMPLATES } from './errorMessageTemplates'

const FORMAT_PARAMETER = /%(?:0?\d*)[dqsvwX]/g
const escapePattern = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const OTA_STATE_LABELS: Record<string, string> = {
  IDLE: '空闲',
  PRECHECK: '预检查',
  TRANSFER: '传输中',
  VERIFY: '校验中',
  APPLY: '应用中',
  SWITCH: '切换固件',
  CONFIRM: '确认中',
  DONE: '已完成',
  FAIL: '失败',
  ENTER_BOOT: '进入引导模式',
  WRITE: '写入固件',
  EXIT_BOOT: '退出引导模式',
  NONE: '无',
  '<NIL>': '未提供',
}

const rules = ERROR_MESSAGE_TEMPLATES.map(([source, translated]) => {
  let cursor = 0
  let pattern = ''
  const parameterKinds: Array<'raw' | 'error' | 'state' | 'boolean'> = []
  for (const match of Array.from(source.matchAll(FORMAT_PARAMETER))) {
    const index = match.index || 0
    pattern += escapePattern(source.slice(cursor, index))
    const type = match[0].slice(-1)
    const prefix = source.slice(0, index)
    const summary = source.startsWith('status=%v active=')
    if (prefix.endsWith('last status: ') || prefix.endsWith('last transfer: ') || (summary && (prefix.endsWith('status=') || prefix.endsWith('peripheral_stage=')))) {
      parameterKinds.push('state')
    } else if (summary && prefix.endsWith('active=')) {
      parameterKinds.push('boolean')
    } else if ((summary && (prefix.endsWith('message=') || prefix.endsWith('last_reject_reason='))) || (!summary && (type === 'w' || type === 'v' || prefix.endsWith('rejected: ') || prefix.endsWith('message=') || prefix.endsWith('), ')))) {
      parameterKinds.push('error')
    } else {
      parameterKinds.push('raw')
    }
    pattern += type === 'd'
      ? '(-?\\d+)'
      : type === 'X'
        ? '([\\dA-Fa-f]+)'
        : type === 'q'
          ? '("(?:[^"\\\\]|\\\\.)*"|`[^`]*`|\\S+)'
          : '([\\s\\S]*?)'
    cursor = index + match[0].length
  }
  pattern += escapePattern(source.slice(cursor))
  return { pattern: new RegExp(`^${pattern}$`, 'i'), translated, parameterKinds, length: source.length }
}).sort((a, b) => b.length - a.length)

const HTTP_ERROR_LABELS: Record<string, string> = {
  400: '请求参数错误',
  401: '未登录或登录已失效',
  403: '权限不足',
  404: '资源不存在',
  409: '操作冲突',
  413: '上传内容过大',
  429: '请求过于频繁',
  500: '服务器内部错误',
  502: '网关错误',
  503: '服务暂不可用',
  504: '网关超时',
}

function translate (text: string, depth: number): string {
  if (depth > 8) return text
  const http = text.match(/^Request failed with status code (\d+)$/i)
  if (http) return `请求失败（HTTP ${http[1]}${HTTP_ERROR_LABELS[http[1]] ? `：${HTTP_ERROR_LABELS[http[1]]}` : ''}）`
  for (const rule of rules) {
    const match = text.match(rule.pattern)
    if (!match) continue
    let index = 0
    return rule.translated.replace(FORMAT_PARAMETER, () => {
      const value = match[++index]
      const kind = rule.parameterKinds[index - 1]
      if (kind === 'state') return OTA_STATE_LABELS[value.toUpperCase()] || translate(value, depth + 1)
      if (kind === 'boolean') return value === 'true' ? '是' : value === 'false' ? '否' : value
      if (kind === 'error') return value === 'none' || value === '<nil>' ? '无' : translate(value, depth + 1)
      return value
    })
  }
  const parts = text.split(/( -> |: |; )/)
  if (parts.length > 1) {
    const translated = parts.map((part, index) => index % 2 === 0 ? translate(part, depth + 1) : part).join('')
    if (translated !== text) return translated
  }
  // 不猜测未知厂商消息；保留其完整原文供定位，不用通用失败掩盖实际原因。
  return text
}

export function localizeErrorMessage (value: unknown, fallback = '操作失败') {
  const text = typeof value === 'string' ? value.trim() : ''
  return text ? translate(text, 0) : fallback
}

export function localizeApiErrorResponse<T> (body: T): T {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return body
  const source = body as Record<string, any>
  const failed = source.success === false || (source.code !== undefined && Number(source.code) !== 0)
  const keys = failed ? ['errorMsg', 'msg', 'message', 'device_message'] : ['errorMsg']
  let result: T = body
  for (const key of keys) {
    if (typeof source[key] !== 'string' || !source[key]) continue
    const translated = localizeErrorMessage(source[key])
    if (translated !== source[key]) {
      if (result === body) result = { ...source } as T
      Object.assign(result as object, { [key]: translated, [`raw_${key}`]: source[`raw_${key}`] ?? source[key] })
    }
  }
  return result
}

export function localizeRequestError<T> (error: T): T {
  if (!error || typeof error !== 'object') return error
  const source = error as Record<string, any>
  if (typeof source.message === 'string') {
    const translated = localizeErrorMessage(source.message)
    if (translated !== source.message) {
      source.rawMessage = source.rawMessage ?? source.message
      source.message = translated
    }
  }
  if (source.response?.data) source.response.data = localizeApiErrorResponse(source.response.data)
  return error
}
